import React, { useState, useEffect, forwardRef, useCallback } from "react";
import classNames from "classnames";
import IEditableProps from "../../utilities/IEditableProps";
import IStyledProps from "../../utilities/IStyledProps";
import EmployeeGrid from "./EmployeeGrid";
import EmployeeSearch from "./EmployeeSearch";
import EmployeeHeader from "./EmployeeHeader";
import { getEmployeesByCode, searchEmployees } from "./services/EmployeeService";
import IEmployee from "./models/IEmployee";
import IGeoPosition from "./models/IGeoPosition";

export interface IEmployeeLookupProps extends IEditableProps, IStyledProps {
    defaultEmployees: string;
    limit: number;
    defaultRadius: number;
    errorMessage: string;
    heading: string;
    subheading: string;
    department: string;
    showSearch: boolean;
    cardClass: string;
    buttonClass: string;
    cardButtonClass: string;
}

const EmployeeLookup = forwardRef(({ defaultEmployees, limit = 4, defaultRadius= 5, cmsId, editorClassObj, errorMessage, heading, subheading, department, showSearch, cardClass, buttonClass, cardButtonClass }: IEmployeeLookupProps, ref: any) => {
    const [userLocation, setUserLocation] = useState<IGeoPosition|null>();
    const [searchParams, setSearchParams] = useState({latitude: '', longitude: '', radius: '', address: ''});
    const [employees, setEmployees] = useState<IEmployee[]>([]);
    const [virtualEmployees, setVirtualEmployees] = useState<IEmployee[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [isSearch, setSearch] = useState(false);
    const [usedSearchRadius, setUsedSearchRadius] = useState<string | number | null>(null);
    const loadMap = process.env.GATSBY_LOAD_MAP === "true";

    function tryGeolocation() {
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(function (position) {
                setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
            });
        }
    }

    const onSearch = useCallback(async (latitude, longitude, radius, address, department) => {
        setSearchParams({latitude, longitude, radius, address});
        setLoading(true);
        const { localEmployees, virtualEmployees, usedSearchRadius: returnedSearchRadius } = await searchEmployees(latitude, longitude, radius, limit, department);
        setVirtualEmployees(virtualEmployees);
        setEmployees(localEmployees);
        if (returnedSearchRadius) {
            setUsedSearchRadius(returnedSearchRadius);
        }
        setLoading(false);
        setSearch(true);
    }, []);

    useEffect(() => {
        if (window) {
            tryGeolocation();
        }
    }, []);

    useEffect(() => {
        async function load() {
            if (defaultEmployees) {
                setLoading(true);
                setEmployees(await getEmployeesByCode(defaultEmployees));
                setLoading(false);
            }
        }
        load();
    }, [defaultEmployees]);

    return (
        <div data-id={cmsId} ref={ref} className={classNames("m-0 w-100", editorClassObj)}>
            <EmployeeHeader heading={heading} subheading={subheading}/>
            {showSearch && <EmployeeSearch loadMap={loadMap} onSearch={onSearch} userLocation={userLocation} defaultRadius={defaultRadius} department={department} buttonClass={buttonClass}/>}
            <EmployeeGrid isSearch={isSearch} isLoading={isLoading} employees={employees} virtualEmployees={virtualEmployees} radius={usedSearchRadius ?? searchParams.radius} address={searchParams.address} errorMessage={errorMessage} cardClass={cardClass} cardButtonClass={cardButtonClass} />
        </div>
    );
});

export default EmployeeLookup;